<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import dayjs from 'dayjs'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import type {
  ConversionPathListData,
  ConversionPathListParams,
  PathRecord,
} from '@/services/api/stastics.api'

import { ExportFloatPanel } from '@/components/data-exporter'
import { DateRangePicker } from '@/components/date-picker'
import Page from '@/components/global-layout/basic-page.vue'
import { Icon } from '@/components/icon'
import SaveViewsButton from '@/components/reports/save-views-button.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTableExport } from '@/composables/use-table-export'
import { useTeam } from '@/composables/use-team'
import { useTeamDateRange } from '@/composables/use-team-date-range'
import { parseSavedViewData, toReportApiScene } from '@/constants/report-library'
import {
  conversionPathList,
  conversionViewList,
} from '@/services/api/stastics.api'
import { useStatisticsStore } from '@/stores/statistics'
import { useUserStore } from '@/stores/user'

import AllAttributeDetailDialog from '../components/all-attribute-detail-dialog.vue'
import AllFilterTags from '../components/all-filter-tags.vue'
import ConversionPathFilter from './components/conversion-path-filter.vue'
import ConversionPathTable from './components/conversion-path-table.vue'
import ConversionPathTouchpoints from './components/conversion-path-touchpoints.vue'

defineOptions({
  name: 'ConversionPathIndex',
})

definePage({
  meta: {
    keepAlive: true,
  },
})

// Types
type SortDirection = 'ascending' | 'descending' | null

interface SearchSort {
  key: string
  sort: SortDirection
}

interface FilterItem {
  key?: string
  operation?: string
  values?: string[]
  display_values?: string[]
  selected_products?: Array<string | number>
}

interface SearchParams {
  date_start: string
  date_end: string
  timezone: string
  goal_code: string
  model_code: string
  dimension: 'channel' | 'campaign'
  window: number
  searchChannel: string
  searchInput: string
  filters: FilterItem[]
  sorts: SearchSort
  page: number
  page_size: number
}

// Composables
const { t } = useI18n()
const userStore = useUserStore()
const statisticsStore = useStatisticsStore()
const { teamTimezone } = useTeam()
const { resolveDefaultRange } = useTeamDateRange()
const router = useRouter()
const route = useRoute()

// State
// 免费版默认显示今天，付费版默认显示过去7天
function getDefaultRange() {
  const isFreeUser = userStore.isFree()
  if (isFreeUser) {
    // 免费版：今天（使用 preset 以避免 days: 0 被强制为 1 的问题）
    return resolveDefaultRange({ type: 'preset', preset: 'today' }, { endDateOffset: 1 })
  }
  else {
    // 付费版：过去7天
    return resolveDefaultRange({ type: 'relative', days: 7, includeToday: false }, { endDateOffset: 1 })
  }
}

const defaultRange = getDefaultRange()
const selectedDateRange = ref({ start: defaultRange.start, end: defaultRange.end })
const pickerDraftRange = ref({ ...selectedDateRange.value })

const tableKey = ref(`path-${Date.now()}`)
const tableData = ref<PathRecord[]>([])
const totalRecords = ref(0)
const sumData = ref<Record<string, any>>({})
const filterShowItems = ref<string[]>([])
const detailDialogRef = ref<InstanceType<typeof AllAttributeDetailDialog> | null>(null)

// Dropdown data from store
const goalsSimpleListData = computed(() => statisticsStore.goalsSimpleList)
const modelSimpleListData = computed(() => statisticsStore.modelSimpleList)
const channelTypesData = computed(() => statisticsStore.basicChannelTypes)

// Dimension options
const dimensionTypes = computed(() => [
  { label: t('conversionPath.dimensions.campaign'), value: 'campaign' },
  { label: t('conversionPath.dimensions.channel'), value: 'channel' },
])

// Search params
const searchParams = reactive<SearchParams>({
  date_start: selectedDateRange.value.start,
  date_end: selectedDateRange.value.end,
  timezone: defaultRange.timezone || teamTimezone.value,
  goal_code: '',
  model_code: '',
  dimension: 'campaign',
  window: 30,
  searchChannel: '',
  searchInput: '',
  filters: [],
  sorts: { key: 'conversions', sort: 'descending' },
  page: 1,
  page_size: 20,
})

// Initialize data from store
async function initializeData() {
  try {
    // Load goals list
    await statisticsStore.getGoalsSimpleList()
    if (goalsSimpleListData.value.length > 0 && !searchParams.goal_code) {
      searchParams.goal_code = goalsSimpleListData.value[0].code
    }

    // Load models list
    await statisticsStore.getModelSimpleList()
    if (modelSimpleListData.value.length > 1 && !searchParams.model_code) {
      searchParams.model_code = modelSimpleListData.value[modelSimpleListData.value.length - 2]?.code || modelSimpleListData.value[0]?.code
    }

    // Load channel types
    await statisticsStore.getTypes()
  }
  catch (e) {
    console.error('Failed to initialize data:', e)
  }
}

onMounted(() => {
  if (userStore.isFree()) {
    router.replace('/attribution/explorer-conversion-path')
    return
  }
  initializeData()
})

const isApplyingSavedView = ref(false)
const appliedSavedViewCode = ref<string | null>(null)
const currentSavedViewName = ref<string | null>(null)
const pageTitleText = computed(() => currentSavedViewName.value || t('conversionPath.pageTitle'))

async function applySavedViewFromQuery() {
  const viewScene = route.query.view_scene
  const viewCode = route.query.view_code
  if (viewScene !== 'path' || typeof viewCode !== 'string' || !viewCode) {
    appliedSavedViewCode.value = null
    currentSavedViewName.value = null
    return
  }
  if (appliedSavedViewCode.value === viewCode)
    return

  isApplyingSavedView.value = true
  try {
    const res = await conversionViewList({ scene: toReportApiScene('path'), page: 1, page_size: 200 })
    const records = res.data?.records || []
    const matched = records.find((r: any) => r.code === viewCode)
    if (!matched) {
      toast.error(t('conversionPath.views.notFound'))
      return
    }
    if (matched.data_json) {
      const parsed = parseSavedViewData(matched.data_json, 'path')
      handleViewChange(parsed.viewData)
      appliedSavedViewCode.value = viewCode
      currentSavedViewName.value = matched.name || null
    }
  }
  catch {
    toast.error(t('conversionPath.views.applyFailed'))
  }
  finally {
    isApplyingSavedView.value = false
  }
}

watch(() => route.query, () => {
  if (userStore.isFree())
    return
  applySavedViewFromQuery()
}, { immediate: true })

// Normalized params for API
const normalizedListParams = computed<ConversionPathListParams>(() => {
  const payload: ConversionPathListParams = {
    page: searchParams.page,
    page_size: searchParams.page_size,
    date_start: dayjs(searchParams.date_start).format('YYYY-MM-DD'),
    date_end: dayjs(searchParams.date_end).format('YYYY-MM-DD'),
    timezone: searchParams.timezone,
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
    dimension: searchParams.dimension,
    window: searchParams.window,
  }

  // Add search based on dimension
  if (searchParams.dimension === 'campaign' && searchParams.searchInput?.trim()) {
    payload.search = searchParams.searchInput.trim()
  }
  if (searchParams.dimension === 'channel' && searchParams.searchChannel) {
    payload.search = searchParams.searchChannel
    payload.searchChannel = searchParams.searchChannel
  }

  // Add filters (only for purchase goal)
  if (searchParams.goal_code === 'x4w0tc0elo0co1n9eftdalo100h8dp6k' && searchParams.filters.length > 0) {
    payload.filters = searchParams.filters
      .filter(f => f.key && f.operation && f.values)
      .map(f => ({
        key: f.key!,
        operation: f.operation!,
        values: f.values!,
      }))
  }

  // Add sorts
  if (searchParams.sorts?.key) {
    const sortValue = searchParams.sorts.sort === 'ascending' ? '1' : '2'
    payload.sorts = [{ key: searchParams.sorts.key, sort: sortValue }]
  }

  return payload
})

// Normalized params for detail dialog (conversionPath)
const normalizedDialogParams = computed(() => {
  const params: any = {
    date_start: dayjs(searchParams.date_start).format('YYYY-MM-DD'),
    date_end: dayjs(searchParams.date_end).format('YYYY-MM-DD'),
    timezone: searchParams.timezone,
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
    dimension: searchParams.dimension,
    window: searchParams.window,
    filters: searchParams.filters,
  }

  // Add search based on dimension (matching list params)
  if (searchParams.dimension === 'campaign' && searchParams.searchInput?.trim()) {
    params.search = searchParams.searchInput.trim()
  }
  if (searchParams.dimension === 'channel' && searchParams.searchChannel) {
    params.search = searchParams.searchChannel
    params.searchChannel = searchParams.searchChannel
  }

  return params
})

// List query - staleTime: 0 ensures fresh data on every parameter change (including sort)
const listQuery = useQuery({
  queryKey: computed(() => ['conversionPathList', normalizedListParams.value]),
  queryFn: async () => {
    if (!searchParams.goal_code || !searchParams.model_code) {
      return { code: 0, data: { records: [], total: 0, page: 1, page_size: 20, sum_result: {} } as ConversionPathListData }
    }
    return await conversionPathList<ConversionPathListData>(normalizedListParams.value)
  },
  enabled: computed(() => Boolean(searchParams.goal_code && searchParams.model_code)),
  staleTime: 0, // Always fetch fresh data from server
  gcTime: 0, // Don't cache query results
})

// Touchpoints query - fetch all pages data for top summary cards
const touchpointsQuery = useQuery({
  queryKey: computed(() => {
    const params = { ...normalizedListParams.value, page: 1, page_size: 2000 }
    // Exclude sort from touchpoints query key since it doesn't affect the overall summary
    delete params.sorts
    return ['conversionPathTouchpoints', params]
  }),
  queryFn: async () => {
    if (!searchParams.goal_code || !searchParams.model_code) {
      return { code: 0, data: { records: [], total: 0, page: 1, page_size: 2000, sum_result: {} } as ConversionPathListData }
    }
    const params = {
      ...normalizedListParams.value,
      page: 1,
      page_size: 2000,
    }
    // Sort is irrelevant for overall statistics, but backend might require it or ignore it.
    return await conversionPathList<ConversionPathListData>(params)
  },
  enabled: computed(() => Boolean(searchParams.goal_code && searchParams.model_code)),
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
})

const touchpointsData = computed(() => {
  return touchpointsQuery.data.value?.data?.records ?? []
})

const touchpointsRenderKey = computed(() => [
  searchParams.goal_code,
  searchParams.model_code,
  searchParams.dimension,
  searchParams.date_start,
  searchParams.date_end,
  searchParams.window,
].join(':'))

// Watch list data
watch(() => listQuery.data.value, (response) => {
  if (!response)
    return
  const records = response.data?.records ?? []
  tableData.value = records
  totalRecords.value = response.data?.total ?? records.length
  // Get sum_result from list response (same as old system)
  sumData.value = response.data?.sum_result ?? {}
  tableKey.value = `path-${Date.now()}`
}, { immediate: true })

// Handlers
function handleDateChange(range?: { start: string, end: string }) {
  if (!range)
    return
  selectedDateRange.value = range
  searchParams.date_start = range.start
  searchParams.date_end = range.end
  searchParams.page = 1
}

function handleSortChange({ prop, order }: { prop: string, order: SortDirection }) {
  if (!order) {
    searchParams.sorts = { key: 'conversions', sort: 'descending' }
  }
  else {
    searchParams.sorts = { key: prop, sort: order }
  }
  searchParams.page = 1
}

function setPage(p: number) {
  const maxPage = Math.max(1, Math.ceil((totalRecords.value || 1) / searchParams.page_size))
  searchParams.page = Math.max(1, Math.min(p, maxPage))
}

function handlePageSizeChange(value: number) {
  searchParams.page_size = value
  searchParams.page = 1
}

function handleDimensionChange() {
  searchParams.searchChannel = ''
  searchParams.searchInput = ''
  searchParams.page = 1
}

function handleSearchInputBlur() {
  searchParams.page = 1
}

function handleRowClick(row: PathRecord) {
  detailDialogRef.value?.onShow('conversions', row)
}

function handleFilterChange(filters: any[], showItems: string[]) {
  searchParams.filters = filters as FilterItem[]
  filterShowItems.value = showItems
  searchParams.page = 1
}

function handleFilterTagClose(index: number) {
  searchParams.filters.splice(index, 1)
  filterShowItems.value.splice(index, 1)
  searchParams.page = 1
}

function handleRefresh() {
  listQuery.refetch()
  touchpointsQuery.refetch()
}

const {
  isExporting,
  exportProgress,
  exportTotal,
  exportAllData,
  exportReady,
  showExportPanel,
  exportFileName,
  startExport,
  downloadCsv,
  cancel: cancelExport,
  closePanel: closeExportPanel,
} = useTableExport({
  filename: () => `conversion-path-${dayjs().format('YYYY-MM-DD_HHmm')}.csv`,
})

function getChannelNameForExport(val: string): string {
  if (!val)
    return val
  if (val === 'organic')
    return 'Organic Search'
  if (val === 'sm')
    return 'Social Media'
  if (val === 'creator')
    return 'Influencer'
  if (val === 'share_a_sale')
    return 'ShareASale'
  if (val === 'agentic_shopping')
    return 'Agentic Shopping'
  if (val === 'unassigned')
    return 'Unassigned'
  if (val.includes('_ad')) {
    if (val === 'ad_google_ad')
      return 'Google Ads'
    if (val === 'amazon_dsp_ad')
      return 'Amazon DSP Ads'
    return val.replace('_ad', ' Ads').replace(/^\w/, c => c.toUpperCase())
  }
  const channelInfo = channelTypesData.value.find((c: any) => c.value === val)
  if (channelInfo)
    return channelInfo.text
  return val.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
}

function formatTimeForExport(seconds: number): string {
  if (!seconds || seconds <= 0)
    return '-'
  const totalSeconds = Math.round(seconds)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  return `${days}${t('conversionPath.export.days')}${hours}${t('conversionPath.export.hours')}${minutes}${t('conversionPath.export.minutes')}${secs}${t('conversionPath.export.seconds')}`
}

async function fetchPageForExport(pageNum: number, pageSize: number): Promise<{ records: PathRecord[], total: number }> {
  const payload: ConversionPathListParams = {
    page: pageNum,
    page_size: pageSize,
    date_start: dayjs(searchParams.date_start).format('YYYY-MM-DD'),
    date_end: dayjs(searchParams.date_end).format('YYYY-MM-DD'),
    timezone: searchParams.timezone,
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
    dimension: searchParams.dimension,
    window: searchParams.window,
  }

  if (searchParams.dimension === 'campaign' && searchParams.searchInput?.trim())
    payload.search = searchParams.searchInput.trim()
  if (searchParams.dimension === 'channel' && searchParams.searchChannel) {
    payload.search = searchParams.searchChannel
    payload.searchChannel = searchParams.searchChannel
  }

  if (searchParams.goal_code === 'x4w0tc0elo0co1n9eftdalo100h8dp6k' && searchParams.filters.length > 0) {
    payload.filters = searchParams.filters
      .filter((f: any) => f.key && f.operation && f.values)
      .map((f: any) => ({ key: f.key, operation: f.operation, values: f.values }))
  }

  if (searchParams.sorts?.key) {
    const sortValue = searchParams.sorts.sort === 'ascending' ? '1' : '2'
    payload.sorts = [{ key: searchParams.sorts.key, sort: sortValue }]
  }

  const response = await conversionPathList<ConversionPathListData>(payload)
  if (response.code > 0) {
    return { records: response.data?.records ?? [], total: response.data?.total ?? 0 }
  }
  throw new Error(response.message || t('conversionPath.export.fetchError'))
}

const MAX_PATH_COLS = 98

function buildExportColumns() {
  const columns: any[] = [
    {
      key: '_index',
      label: t('conversionPath.export.columns.index'),
      formatter: (_row: any, rowIndex: number) => String(rowIndex + 1),
    },
    {
      key: 'time_diff',
      label: t('conversionPath.export.columns.timeDiff'),
      formatter: (row: any) => formatTimeForExport(row.time_diff || 0),
    },
    {
      key: 'touch_points',
      label: t('conversionPath.export.columns.touchPoints'),
      formatter: (row: any) => String(row.touch_points || 0),
    },
    {
      key: 'conversion_value',
      label: t('conversionPath.export.columns.revenue'),
      formatter: (row: any) => String(row.conversion_value || 0),
    },
    {
      key: 'conversions',
      label: t('conversionPath.export.columns.conversions'),
      formatter: (row: any) => String(row.conversions || 0),
    },
  ]

  for (let i = 1; i <= MAX_PATH_COLS; i++) {
    columns.push({
      key: `_channel_${i}`,
      label: `${t('conversionPath.export.columns.channel')}${i}`,
      formatter: (row: any) => {
        const path = (row.event_final_path || []) as any[]
        const item = path[i - 1]
        if (!item)
          return ''
        return searchParams.dimension === 'campaign'
          ? (item.event_campaign || '')
          : getChannelNameForExport(item.event_type || '')
      },
    })
    columns.push({
      key: `_channel_${i}_num`,
      label: t('conversionPath.export.columns.visitCount'),
      formatter: (row: any) => {
        const path = (row.event_final_path || []) as any[]
        const item = path[i - 1]
        if (!item)
          return ''
        return String(item.event_num || 1)
      },
    })
  }

  return columns
}

async function handleExport() {
  const columns = buildExportColumns()
  try {
    await startExport(fetchPageForExport, columns)
  }
  catch (error: any) {
    toast.error(error?.message || t('conversionPath.export.fetchError'))
  }
}

function handleDownloadCsv() {
  downloadCsv(buildExportColumns())
}

function handleViewChange(viewData: any) {
  if (viewData?.searchParams) {
    const sp = viewData.searchParams
    if (sp.goal_code)
      searchParams.goal_code = sp.goal_code
    if (sp.model_code)
      searchParams.model_code = sp.model_code
    if (sp.dimension)
      searchParams.dimension = sp.dimension
    if (sp.window)
      searchParams.window = sp.window
    if (sp.filters)
      searchParams.filters = sp.filters
    if (sp.sorts)
      searchParams.sorts = sp.sorts
  }
}

// Computed for custom views
const customViewDataJson = computed(() => ({
  searchParams: {
    goal_code: searchParams.goal_code,
    model_code: searchParams.model_code,
    dimension: searchParams.dimension,
    window: searchParams.window,
    filters: searchParams.filters,
    sorts: searchParams.sorts,
  },
}))

function getCurrentViewData() {
  return customViewDataJson.value
}

// No data text
const noDataText = computed(() => {
  const goal = goalsSimpleListData.value.find(g => g.code === searchParams.goal_code)?.name || 'purchase'
  return t('conversionPath.messages.noDataFound', { goal: goal.toLowerCase() })
})

// Loading states
const isTableLoading = computed(() => listQuery.isLoading.value || listQuery.isFetching.value)
</script>

<template>
  <Page :title="pageTitleText" :description="t('conversionPath.description')" sticky header-class="!rounded-none !border-b border-b-[color:var(--p-color-border-subdued)]" class="conversion-path-page cf-fullwidth min-h-[calc(100vh-61px)] bg-[#F1F1F1] px-[--p-space-600] md:px-[--p-space-1200] p-[--p-space-600] !mt-0 !mx-0 !mb-0 !rounded-none !border-0 !bg-transparent !shadow-none !p-0 pt-[var(--p-space-400)]">
    <template #title>
      <div class="flex items-center gap-2">
        <span>{{ pageTitleText }}</span>
        <Badge v-if="currentSavedViewName" variant="secondary">
          {{ t('saveViews.appliedBadge') }}
        </Badge>
      </div>
    </template>
    <template #actions>
      <div class="flex items-center gap-2">
        <span class="bfsp-cp-saveviews-wrap"><SaveViewsButton scene="path" :get-current-view-data="getCurrentViewData" /></span>
        <span class="bfsp-cp-refresh-wrap"><Button variant="outline" size="sm" class="cta-secondary" @click="handleRefresh">
          <Icon name="lucide:refresh-cw" class="mr-1 h-4 w-4" />
          {{ t('conversionPath.refresh') }}
        </Button></span>
      </div>
    </template>

    <!-- Filter Bar - Single Row Layout -->
    <div class="mb-6 flex flex-wrap items-center gap-3">
      <!-- Conversion Goal -->
      <Select v-model="searchParams.goal_code">
        <SelectTrigger class="w-[140px]">
          <SelectValue :placeholder="t('conversionPath.conversionGoal')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="goal in goalsSimpleListData"
            :key="goal.code"
            :value="goal.code"
          >
            {{ goal.name }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Date Range Picker -->
      <DateRangePicker
        v-model="pickerDraftRange"
        :timezone="teamTimezone"
        :end-date-offset="1"
        :is-plan-picker="true"
        auth-key="conversion_path"
        class="w-[200px]"
        @refresh="handleDateChange"
      />

      <!-- Dimension -->
      <Select v-model="searchParams.dimension" @update:model-value="handleDimensionChange">
        <SelectTrigger class="w-[120px]">
          <SelectValue :placeholder="t('conversionPath.dimension')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="dim in dimensionTypes"
            :key="dim.value"
            :value="dim.value"
          >
            {{ dim.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Channel Filter (when dimension is channel) -->
      <div v-if="searchParams.dimension === 'channel'" class="flex items-center gap-1">
        <Select v-model="searchParams.searchChannel">
          <SelectTrigger class="w-[140px]">
            <SelectValue :placeholder="t('conversionPath.select')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="channel in channelTypesData"
              :key="channel.value"
              :value="channel.value"
            >
              {{ channel.text }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Button
          v-if="searchParams.searchChannel"
          variant="ghost"
          size="icon"
          class="h-8 w-8 cta-secondary-inline"
          @click="searchParams.searchChannel = ''"
        >
          <Icon name="lucide:x" class="h-4 w-4" />
        </Button>
      </div>

      <!-- Campaign Search (when dimension is campaign) -->
      <Input
        v-if="searchParams.dimension === 'campaign'"
        v-model="searchParams.searchInput"
        :placeholder="t('conversionPath.searchCampaign')"
        class="w-[160px]"
        @keyup.enter="handleSearchInputBlur"
        @blur="handleSearchInputBlur"
      >
        <template #prefix>
          <Icon name="lucide:search" class="h-4 w-4 text-muted-foreground" />
        </template>
      </Input>

      <!-- Attribution Model -->
      <Select v-model="searchParams.model_code">
        <SelectTrigger class="w-[140px]">
          <SelectValue :placeholder="t('conversionPath.attributionModel')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="model in modelSimpleListData"
            :key="model.code"
            :value="model.code"
          >
            {{ model.name }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Help Icon -->
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Icon name="lucide:help-circle" class="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent side="top" class="max-w-[300px]">
            <p>{{ t('conversionPath.helpTooltip') }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <!-- Advanced Filter (only for purchase goal) -->
      <ConversionPathFilter
        v-if="searchParams.goal_code === 'x4w0tc0elo0co1n9eftdalo100h8dp6k'"
        :model-value="searchParams.filters"
        :channel-types="channelTypesData"
        @update:model-value="handleFilterChange"
      />

      <span class="bfsp-cp-export-wrap"><Button variant="outline" size="sm" class="cta-secondary" :disabled="isExporting || !searchParams.goal_code || !searchParams.model_code" @click="handleExport">
        <Icon name="lucide:download" class="mr-1 h-4 w-4" />
        {{ t('conversionPath.export.button') }}
      </Button></span>
    </div>

    <!-- Filter Tags -->
    <div v-if="filterShowItems.length > 0" class="mb-6 rounded-[12px] border border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] p-4 shadow-[var(--p-shadow-100)]">
      <AllFilterTags
        :filters="filterShowItems"
        @close="handleFilterTagClose"
      />
    </div>

    <!-- Touchpoints Visualization -->
    <Skeleton v-if="touchpointsQuery.isLoading.value" class="mb-6 h-[200px] w-full rounded-[12px]" />
    <div v-else-if="touchpointsData.length > 0" class="mb-6 rounded-[12px] border border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] p-4 shadow-[var(--p-shadow-100)]">
      <ConversionPathTouchpoints
        :key="touchpointsRenderKey"
        :data="touchpointsData"
        :dimension="searchParams.dimension"
        metric="conversions"
      />
    </div>

    <!-- Table View -->
    <div class="rounded-[12px] border border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] shadow-[var(--p-shadow-100)]">
      <Skeleton v-if="isTableLoading" class="m-4 h-[400px] w-full" />
      <template v-else>
        <ConversionPathTable
          :key="tableKey"
          :table-data="tableData"
          :sum-data="sumData"
          :dimension-type="searchParams.dimension"
          :page="{ current: searchParams.page, size: searchParams.page_size, total: totalRecords }"
          :search-params="searchParams"
          :no-data-text="noDataText"
          :channel-types="channelTypesData"
          @sort-change="handleSortChange"
          @row-click="handleRowClick"
        />

        <!-- Pagination -->
        <div v-if="totalRecords > 0" class="flex items-center justify-between border-t px-4 py-3">
          <div class="text-sm text-muted-foreground">
            {{ t('conversionPath.pagination.showing') }} {{ (searchParams.page - 1) * searchParams.page_size + 1 }} {{ t('conversionPath.pagination.to') }} {{ Math.min(searchParams.page * searchParams.page_size, totalRecords) }} {{ t('conversionPath.pagination.of') }} {{ totalRecords }} {{ t('conversionPath.pagination.results') }}
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              class="cta-secondary-pagination"
              :disabled="searchParams.page <= 1"
              @click="setPage(searchParams.page - 1)"
            >
              {{ t('conversionPath.pagination.previous') }}
            </Button>
            <span class="text-sm">{{ t('conversionPath.pagination.page') }} {{ searchParams.page }} {{ t('conversionPath.pagination.of') }} {{ Math.ceil(totalRecords / searchParams.page_size) }}</span>
            <Button
              variant="outline"
              size="sm"
              class="cta-secondary-pagination"
              :disabled="searchParams.page >= Math.ceil(totalRecords / searchParams.page_size)"
              @click="setPage(searchParams.page + 1)"
            >
              {{ t('conversionPath.pagination.next') }}
            </Button>
          </div>
        </div>
      </template>
    </div>

    <!-- Detail Dialog -->
    <AllAttributeDetailDialog
      ref="detailDialogRef"
      :search-params="normalizedDialogParams"
      object-type="conversionPath"
    />

    <ExportFloatPanel
      :show="showExportPanel"
      :is-exporting="isExporting"
      :export-ready="exportReady"
      :progress="exportProgress"
      :total="exportTotal"
      :row-count="exportAllData.length"
      :file-name="exportFileName"
      @cancel="cancelExport"
      @download="handleDownloadCsv"
      @close="closeExportPanel"
    />
  </Page>
</template>

<style scoped>
.conversion-path-page :deep([data-slot="button"]):focus-visible,
.conversion-path-page :deep([role="button"]):focus-visible,
.conversion-path-page :deep(button):focus-visible,
.conversion-path-page :deep(a[href]):focus-visible,
.conversion-path-page :deep(input):focus-visible,
.conversion-path-page :deep(select):focus-visible,
.conversion-path-page :deep(textarea):focus-visible,
.conversion-path-page :deep([tabindex]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  padding-left: var(--p-space-400);
  padding-right: var(--p-space-400);
}
.conversion-path-page :deep(.cta-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.conversion-path-page :deep(.cta-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.conversion-path-page :deep(.cta-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-secondary) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
  padding-left: var(--p-space-400) !important;
  padding-right: var(--p-space-400) !important;
}
.conversion-path-page :deep(.cta-secondary):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-subdued) !important;
}
.conversion-path-page :deep(.cta-secondary):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-subdued) !important;
}
.conversion-path-page :deep(.cta-secondary):focus-visible {
  border-color: var(--p-color-border-subdued) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-secondary-inline) {
  border-radius: var(--p-border-radius-100) !important;
  background: transparent !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.conversion-path-page :deep(.cta-secondary-inline):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.conversion-path-page :deep(.cta-secondary-inline):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
}
.conversion-path-page :deep(.cta-secondary-inline):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-secondary-pagination) {
  height: 32px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  padding-left: var(--p-space-300) !important;
  padding-right: var(--p-space-300) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-subdued) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-subdued) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):focus-visible {
  border-color: var(--p-color-border-subdued) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.conversion-path-page :deep(input) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  background: var(--p-color-bg-fill) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  color: var(--p-color-text) !important;
  font-size: 13px !important;
}
.conversion-path-page :deep(input):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep([data-slot="table-header"]) {
  background: var(--p-color-bg-surface-secondary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  color: var(--p-color-text-secondary) !important;
}

.conversion-path-page :deep([data-slot="table-row"]) {
  background: var(--p-color-bg-surface) !important;
}
.conversion-path-page :deep([data-slot="table-row"]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
}

.conversion-path-page :deep(.text-secondary),
.conversion-path-page :deep(.text-muted-foreground) {
  color: var(--p-color-text-secondary) !important;
}

.cf-fullwidth {
  width: calc(100% + 2 * var(--p-space-600)) !important;
  position: relative !important;
  left: calc(-1 * var(--p-space-600)) !important;
  max-width: none !important;
  padding-left: var(--p-space-600) !important;
  padding-right: var(--p-space-600) !important;
  padding-top: var(--p-space-600) !important;
  padding-bottom: var(--p-space-600) !important;
  box-sizing: border-box !important;
}
@media (min-width: 768px) {
  .cf-fullwidth {
    width: calc(100% + 2 * var(--p-space-1200)) !important;
    left: calc(-1 * var(--p-space-1200)) !important;
    padding-left: var(--p-space-1200) !important;
    padding-right: var(--p-space-1200) !important;
    padding-top: var(--p-space-600) !important;
    padding-bottom: var(--p-space-600) !important;
  }
}

.conversion-path-page {
  background-color: #F1F1F1 !important;
}

.conversion-path-page :deep([data-slot="card-header"]) {
  padding: var(--p-space-400) var(--p-space-400) 8px var(--p-space-400) !important;
}
.conversion-path-page :deep([data-slot="card-content"]) {
  padding: 0px var(--p-space-400) var(--p-space-400) var(--p-space-400) !important;
}

.conversion-path-page :deep([data-slot="button"]):focus-visible,
.conversion-path-page :deep([role="button"]):focus-visible,
.conversion-path-page :deep(button):focus-visible,
.conversion-path-page :deep(a[href]):focus-visible,
.conversion-path-page :deep(input):focus-visible,
.conversion-path-page :deep(select):focus-visible,
.conversion-path-page :deep(textarea):focus-visible,
.conversion-path-page :deep([tabindex]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep([data-slot="card-content"] [data-slot="button"]) {
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.conversion-path-page :deep([data-slot="card-content"] [data-slot="button"]):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.conversion-path-page :deep([data-slot="card-content"] [data-slot="button"]):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border) !important;
}
.conversion-path-page :deep([data-slot="card-content"] [data-slot="button"]):focus-visible {
  border-color: var(--p-color-border) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  height: 36px;
  border-radius: var(--p-border-radius-100);
}
.conversion-path-page :deep(.cta-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.conversion-path-page :deep(.cta-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.conversion-path-page :deep(.cta-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep([data-slot="table-header"]) {
  background: var(--p-color-bg-surface-secondary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  font-size: 11px !important;
}

.conversion-path-page :deep([data-slot="table-row"]) {
  background: var(--p-color-bg-surface) !important;
}
.conversion-path-page :deep([data-slot="table-row"]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
}

.conversion-path-page :deep(.cta-secondary) {
  height: 36px !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  padding: 0 var(--p-space-400) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  outline: none !important;
  transition: background-color 100ms ease-out !important;
}
.conversion-path-page :deep(.cta-secondary):hover {
  background: var(--p-color-bg-fill-hover) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  filter: none !important;
}
.conversion-path-page :deep(.cta-secondary):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  filter: none !important;
}
.conversion-path-page :deep(.cta-secondary):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-secondary-inline) {
  background: transparent !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  box-shadow: none !important;
  outline: none !important;
  transition: background-color 100ms ease-out !important;
}
.conversion-path-page :deep(.cta-secondary-inline):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  filter: none !important;
}
.conversion-path-page :deep(.cta-secondary-inline):active {
  background: var(--p-color-bg-fill-active) !important;
}
.conversion-path-page :deep(.cta-secondary-inline):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.cta-secondary-pagination) {
  height: 32px !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  padding: 0 var(--p-space-300) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  outline: none !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  filter: none !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):active {
  background: var(--p-color-bg-fill-active) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}
.conversion-path-page :deep(.cta-secondary-pagination):disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.conversion-path-page :deep([data-slot="input"]) {
  height: 36px !important;
  background: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-radius: var(--p-border-radius-100) !important;
  color: var(--p-color-text) !important;
  font-size: 14px !important;
}
.conversion-path-page :deep([data-slot="input"]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-border-strong) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(tbody td button),
.conversion-path-page :deep(tbody td a) {
  color: var(--p-color-text-link) !important;
  text-decoration: underline;
  cursor: pointer;
}

/* ================ BfS ConversionPath 按钮终极修 · span壳通杀 + 双锚 + 字面量 ================ */
.conversion-path-page .bfsp-cp-saveviews-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }
.conversion-path-page .bfsp-cp-refresh-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }
.conversion-path-page .bfsp-cp-export-wrap * { border-radius:4px !important; -webkit-border-radius:4px !important; border-top-left-radius:4px !important; border-top-right-radius:4px !important; border-bottom-left-radius:4px !important; border-bottom-right-radius:4px !important; }

.conversion-path-page :deep(.bfsp-cp-saveviews-wrap button[data-slot=button]),
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap [data-slot=button]),
.conversion-path-page :deep(.bfsp-cp-refresh-wrap button[data-slot=button]),
.conversion-path-page :deep(.bfsp-cp-refresh-wrap [data-slot=button]),
.conversion-path-page :deep(.bfsp-cp-export-wrap button[data-slot=button]),
.conversion-path-page :deep(.bfsp-cp-export-wrap [data-slot=button]) {
  height:36px !important; border-radius:4px !important; border:1px solid var(--p-color-border-subdued) !important;
  background:#FFFFFF !important; color:#1A1A1A !important; padding:0 16px !important;
  font-size:13px !important; font-weight:500 !important; box-shadow:none !important; outline:none !important;
}
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap button[data-slot=button]):hover,
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap [data-slot=button]):hover,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap button[data-slot=button]):hover,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap [data-slot=button]):hover,
.conversion-path-page :deep(.bfsp-cp-export-wrap button[data-slot=button]):hover,
.conversion-path-page :deep(.bfsp-cp-export-wrap [data-slot=button]):hover {
  background:#F1F1F1 !important; border:1px solid var(--p-color-border-subdued) !important; box-shadow:none !important; filter:none !important;
}
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap button[data-slot=button]):active,
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap [data-slot=button]):active,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap button[data-slot=button]):active,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap [data-slot=button]):active,
.conversion-path-page :deep(.bfsp-cp-export-wrap button[data-slot=button]):active,
.conversion-path-page :deep(.bfsp-cp-export-wrap [data-slot=button]):active {
  background:#E5E7EB !important; border:1px solid var(--p-color-border-subdued) !important; box-shadow:none !important; filter:none !important;
}
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap button[data-slot=button]):focus-visible,
.conversion-path-page :deep(.bfsp-cp-saveviews-wrap [data-slot=button]):focus-visible,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap button[data-slot=button]):focus-visible,
.conversion-path-page :deep(.bfsp-cp-refresh-wrap [data-slot=button]):focus-visible,
.conversion-path-page :deep(.bfsp-cp-export-wrap button[data-slot=button]):focus-visible,
.conversion-path-page :deep(.bfsp-cp-export-wrap [data-slot=button]):focus-visible {
  outline:none !important; border-color:#008060 !important;
  box-shadow:0 0 0 2px #FFFFFF,0 0 0 5px rgba(4,123,93,0.35) !important;
}
.conversion-path-page :deep([data-slot=select-trigger]),
.conversion-path-page :deep([data-slot=combobox-trigger]),
.conversion-path-page :deep([data-slot=filter-trigger]),
.conversion-path-page :deep([data-slot=popover-trigger]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  background-color: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  height: 36px !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
}
.conversion-path-page :deep([data-slot=toggle-group-item]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  background-color: var(--p-color-bg-surface) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
}
.conversion-path-page :deep([data-slot=select-trigger]):hover,
.conversion-path-page :deep([data-slot=combobox-trigger]):hover,
.conversion-path-page :deep([data-slot=filter-trigger]):hover,
.conversion-path-page :deep([data-slot=popover-trigger]):hover,
.conversion-path-page :deep([data-slot=toggle-group-item]):hover {
  background-color: var(--p-color-bg) !important;
  box-shadow: none !important;
  filter: none !important;
}
.conversion-path-page :deep([data-slot=select-trigger]):focus-visible,
.conversion-path-page :deep([data-slot=combobox-trigger]):focus-visible,
.conversion-path-page :deep([data-slot=filter-trigger]):focus-visible,
.conversion-path-page :deep([data-slot=popover-trigger]):focus-visible,
.conversion-path-page :deep([data-slot=toggle-group-item]):focus-visible {
  outline: none !important;
  border-color: #008060 !important;
  box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.conversion-path-page :deep(.filter-btn),
.conversion-path-page :deep(.Filter > button[data-slot=button]),
.conversion-path-page :deep(.PopoverTrigger > button[data-slot=button]),
.conversion-path-page :deep(button[role=combobox]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
}
.conversion-path-page :deep([role=dialog] button[data-slot=button].cta-primary-success),
.conversion-path-page :deep([role=dialog] button[data-slot=button]:last-child:not(.cta-secondary)),
.conversion-path-page :deep([role=dialog] button[type=submit]:not(.cta-secondary)) {
  background: #008060 !important;
  color: #FFFFFF !important;
  border: none !important;
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
  box-shadow: none !important;
  outline: none !important;
}
.conversion-path-page :deep([role=dialog] button[data-slot=button].cta-primary-success):hover,
.conversion-path-page :deep([role=dialog] button[type=submit]:not(.cta-secondary)):hover {
  background: rgba(0, 128, 96, 0.92) !important;
  filter: none !important;
}
.conversion-path-page :deep([role=dialog] button[data-slot=button].cta-secondary),
.conversion-path-page :deep([role=dialog] button[data-slot=button][variant=outline]),
.conversion-path-page :deep([role=dialog] button[variant=outline]) {
  background: #FFFFFF !important;
  color: #1A1A1A !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  height: 36px !important;
  box-shadow: none !important;
  outline: none !important;
}
.conversion-path-page :deep([role=dialog] button[data-slot=button].cta-secondary):hover,
.conversion-path-page :deep([role=dialog] button[variant=outline]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
  filter: none !important;
}
.conversion-path-page :deep([role=dialog] [data-slot=select-trigger]),
.conversion-path-page :deep([role=dialog] [data-slot=combobox-trigger]),
.conversion-path-page :deep([role=dialog] [data-slot=command-input]),
.conversion-path-page :deep([role=dialog] input),
.conversion-path-page :deep([role=dialog] [data-slot=input]) {
  border-top-left-radius: 4px !important;
  border-top-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border-color: var(--p-color-border-subdued) !important;
  background: var(--p-color-bg-surface) !important;
  height: 36px !important;
}
</style>
